import { FormHelperText, TextField, IconButton, Button, InputAdornment } from "@mui/material";
import { NodeConfig } from "../../types/nodes";
import { WorkflowNode, InputDataRow } from "../../types/workflow";
import RunConfigField from "./RunConfigField";
import { SetStateAction, useMemo, useState, useEffect } from "react";
import { ResponseConfigOption } from "../../types/common";
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { extractNodeID } from '../extractNodeIDs';
import DropdownPopper from '../Common/DropdownPopper';
import InputTextBoxWithDropdown from '../Common/InputTextBoxWithDropdown';
import customFetch from "../../utils/customFetch";

type TriggerSetupProps = {
    openConditionsDetails: boolean,
    localConditions: any,
    removeCondition: any,
    setLocalConditions: any,
    responseConfig: any,
    isAnyStepIncomplete: boolean,
    setConditionsOpenDetails: (openDetails: boolean) => void,
    addNewCondition: () => void,
    onSave: (action?: string, cb?: () => void, isIntermittentSave?: boolean) => void,
    workflowNode: WorkflowNode,
    nodeRunConfig: Record<string, string | null>,
    setNodeRunConfigAndRefNodeId: (key: string, value: string, refNodeId?: string) => void,
    nodeConfig: NodeConfig,
    saveConfigField: (key: string) => void,
    chosenAction: string,
    resetNodeRunConfigInRef: (run_config: Record<string, string>, run_config_metadata: Record<string, {ref_node_id: string}>) => void,
    inputRows: InputDataRow[],
    setInputRows: (value: SetStateAction<InputDataRow[]>) => void,
    filterText: string,
    nodeId: string,
    countToNameMap: Record<string, string>,
    isFetchingSavedNodeConfig: boolean,
};

const getDisplayValue = (key: string, nodeRunConfig: Record<string, string | null>): string => {
    if (nodeRunConfig && key in nodeRunConfig) {
        return (nodeRunConfig as Record<string, unknown>)?.[key]?.toString().trim() || '';
    }

    return '';
};

const TriggerSetupNanonetsUpdate = ({
    nodeRunConfig,
    setNodeRunConfigAndRefNodeId,
    nodeConfig,
    responseConfig,
    chosenAction,
    saveConfigField,
    openConditionsDetails,
    localConditions,
    removeCondition,
    setLocalConditions,
    isAnyStepIncomplete,
    setConditionsOpenDetails,
    addNewCondition,
    onSave,
    workflowNode,
    resetNodeRunConfigInRef,
    inputRows,
    setInputRows,
    filterText,
    nodeId,
    countToNameMap,
    isFetchingSavedNodeConfig,
}: TriggerSetupProps) => {
    const { run_config_metadata, run_config_render_data } = nodeConfig || {};
    const shouldRender = (nodeRunConfig && !!Object.keys(nodeRunConfig)?.length);
    const [fieldOptions, setFieldOptions] = useState<Array<{label: string, value: string}>>([]);
    const [isLoadingFields, setIsLoadingFields] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [activeRowIndex, setActiveRowIndex] = useState<number | null>(null);
    const [currentNodeId, setCurrentNodeId] = useState<string | null>(null);

    const dropdownOptionIdToOptionMap = useMemo(() => {
        let dropdownOptionIdToOptionMap: Record<string, ResponseConfigOption> = {};
        if (responseConfig?.length) {
            responseConfig.forEach((option: ResponseConfigOption) => {
                dropdownOptionIdToOptionMap[`${option.id}:${option.refNodeId}`] = {
                    ...option,
                    value: typeof option.value !== "object" ? option.value : ""
                }
            });
        }
        return dropdownOptionIdToOptionMap;
    }, [responseConfig]);

    // Fetch field options when API key and model ID change
    useEffect(() => {
        const apiKey = nodeRunConfig?.api_key;
        const modelId = nodeRunConfig?.model_id;

        // Check if values are template expressions (from previous steps)
        const isApiKeyTemplate = apiKey?.includes('{{ jp');
        const isModelIdTemplate = modelId?.includes('{{ jp');

        // Don't fetch if either value is from a template
        if (isApiKeyTemplate || isModelIdTemplate) {
            setFieldOptions([]);
            setIsLoadingFields(false);
            return;
        }

        if (apiKey && modelId) {
            setIsLoadingFields(true);
            customFetch('/v1/nanonets/model_details', {
                method: 'POST',
                body: JSON.stringify({
                    api_key: apiKey,
                    model_id: modelId,
                }),
            })
                .then(response => {
                    console.log('Response status:', response.status);
                    return response.json();
                })
                .then(data => {
                    // Extract labels from the model details response
                    const labels = (data.labels || []).map((label: string) => ({
                        label: label,
                        value: label
                    }));
                    setFieldOptions(labels);
                })
                .catch(error => {
                    console.error('Error fetching model details:', error);
                    setFieldOptions([]);
                })
                .finally(() => {
                    setIsLoadingFields(false);
                });
        } else {
            setFieldOptions([]);
            setIsLoadingFields(false);
        }
    }, [nodeRunConfig?.api_key, nodeRunConfig?.model_id]);
    
    // Filter out the basic config fields to show in regular fields
    const filteredNodeRunConfig = useMemo(() => {
        const keysToFilter = ['api_key', 'model_id', 'file_id'];
        return Object.entries(nodeRunConfig || {}).reduce((acc, [key, value]) => {
            if (!keysToFilter.includes(key)) {
                acc[key] = value;
            }
            return acc;
        }, {} as Record<string, string | null>);
    }, [nodeRunConfig]);

    // Only show the basic config fields (not the dynamic fields)
    const filteredKeysNodeRunConfig = useMemo(() => {
        const keysToFilter = ['api_key', 'model_id', 'file_id'];
        return Object.entries(nodeRunConfig || {}).reduce((acc, [key, value]) => {
            if (keysToFilter.includes(key)) {
                acc[key] = value;
            }
            return acc;
        }, {} as Record<string, string | null>);
    }, [nodeRunConfig]);

    const keysToRender = useMemo(() => {
        return new Set([
            ...(filteredKeysNodeRunConfig && Object.keys(filteredKeysNodeRunConfig)?.length ? Object.keys(filteredKeysNodeRunConfig) : [])
        ]);
    }, [filteredKeysNodeRunConfig]);

    const getDisplayOrderVal = (value: number | undefined) => {
        if (typeof value === 'number') {
            return value;
        }

        return 100000;
    }

    // Initialize input rows from config
    useEffect(() => {
        setInputRows((prevInputRows) => {
            let rows = prevInputRows.slice();
            if (nodeId !== currentNodeId) {
                rows = [];
            }

            Object.entries(filteredNodeRunConfig)
                .filter(([key]) => key !== 'code' && key !== 'post_confirmation_endpoint')
                .forEach(([key, value]) => {
                    const { nodeId, dataPath } = extractNodeID(value || "") || {};
                    const refNodeId = nodeId || run_config_metadata?.[key]?.ref_node_id;
                    const optionId = dataPath || value?.slice(7, -4);
                    const existingRow = rows.find(row => row.key === key);
                    if (!existingRow) {
                        rows.push({
                            key,
                            value: value ?? '',
                            refNodeId,
                            optionId
                        });
                    }
                });

            return rows.length ? rows : [{ key: '', value: '', refNodeId: '', optionId: '' }];
        });
        
        setCurrentNodeId(nodeId);
    }, [filteredNodeRunConfig, nodeId, currentNodeId, run_config_metadata]);

    const handleAddRow = () => {
        setInputRows([...inputRows, { key: '', value: '', refNodeId: '', optionId: '' }]);
    };

    const handleDeleteRow = (index: number) => {
        const newRows = [...inputRows];
        const keyToDelete = newRows[index].key;
        
        // Remove from config if key exists
        if (keyToDelete) {
            setNodeRunConfigAndRefNodeId(keyToDelete, '', '');
        }
        
        newRows.splice(index, 1);
        setInputRows(newRows);
        onSave("", () => {}, true);
    };

    const handleKeyChange = (index: number, newKey: string) => {
        const newRows = [...inputRows];
        const oldKey = newRows[index].key;
        newRows[index].key = newKey;
        setInputRows(newRows);
        
        // If there was an old key, remove it from config
        if (oldKey && oldKey !== newKey) {
            setNodeRunConfigAndRefNodeId(oldKey, '', '');
        }
        
        // Save the new key-value pair to config
        if (newKey && newRows[index].value) {
            setNodeRunConfigAndRefNodeId(newKey, newRows[index].value, newRows[index].refNodeId || '');
        }
    };

    const handleValueChange = (index: number, value: string, refNodeId?: string) => {
        const newRows = [...inputRows];
        newRows[index].value = value;
        newRows[index].refNodeId = refNodeId || '';
        newRows[index].optionId = refNodeId ? value : '';
        setInputRows(newRows);
        
        // Save to config if key exists
        if (newRows[index].key) {
            setNodeRunConfigAndRefNodeId(
                newRows[index].key, 
                value, 
                refNodeId || ''
            );
        }
    };

    if (!shouldRender) return <FormHelperText>No details for this action</FormHelperText>;

    return (
        <>
            {Array.from(keysToRender).sort((a, b) => getDisplayOrderVal(run_config_render_data?.[a]?.display_order) - getDisplayOrderVal(run_config_render_data?.[b]?.display_order)).map(key => (
                <RunConfigField
                    key={key}
                    keyName={key}
                    renderConfig={run_config_render_data?.[key] || { type: 'text' }}
                    refNodeId={run_config_metadata?.[key]?.ref_node_id || ''}
                    setNodeRunConfigAndRefNodeId={setNodeRunConfigAndRefNodeId}
                    responseConfig={responseConfig}
                    displayValue={getDisplayValue(key, nodeRunConfig)}
                    saveConfigField={saveConfigField}
                    openConditionsDetails={openConditionsDetails}
                    localConditions={localConditions}
                    removeCondition={removeCondition}
                    setLocalConditions={setLocalConditions}
                    isAnyStepIncomplete={isAnyStepIncomplete}
                    setConditionsOpenDetails={setConditionsOpenDetails}
                    addNewCondition={addNewCondition}
                    onSave={onSave}
                    workflowNode={workflowNode}
                    filterText={filterText}
                    countToNameMap={countToNameMap}
                    isFetchingSavedNodeConfig={isFetchingSavedNodeConfig}
                />
            ))}
            <div className="font-medium text-sm mb-2">Fields</div>
            <div className="p-4 pt-0 space-y-4">
                {inputRows.map((row, index) => (
                    <div key={index}>
                        <div className="flex gap-4 items-start mb-2">
                            <div className="flex-1">
                                <TextField
                                    size="small"
                                    placeholder={isLoadingFields ? "Loading fields..." : "field name"}
                                    value={row.key}
                                    onChange={(e) => handleKeyChange(index, e.target.value)}
                                    onBlur={() => onSave("", () => {}, true)}
                                    fullWidth
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: "0.5rem"
                                        }
                                    }}
                                    InputProps={{
                                        endAdornment: fieldOptions.length > 0 ? (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setAnchorEl(e.currentTarget);
                                                        setActiveRowIndex(index);
                                                    }}
                                                    edge="end"
                                                >
                                                    <ArrowDropDownIcon />
                                                </IconButton>
                                            </InputAdornment>
                                        ) : undefined,
                                    }}
                                />
                            </div>
                            <IconButton onClick={() => handleDeleteRow(index)}>
                                <DeleteIcon />
                            </IconButton>
                        </div>
                        
                        <div className="mb-4 flex gap-4">
                            <InputTextBoxWithDropdown
                                editableInput={row.value || ''}
                                setEditableValue={(value, refNodeId) => {
                                    handleValueChange(index, value, refNodeId);
                                }}
                                refNodeId={row.refNodeId || ''}
                                dropdownOptions={responseConfig}
                                cssOverrides="w-full"
                                saveOnBlur={() => {
                                    onSave("", () => {}, true);
                                }}
                                dropdownOptionIdToOptionMap={dropdownOptionIdToOptionMap}
                                countToNameMap={countToNameMap}
                            />
                            <div className="p-5"></div>
                        </div>

                        {index < inputRows.length - 1 && (
                            <div className="border-t border-gray-200 mb-4"></div>
                        )}
                    </div>
                ))}

                {/* Key name dropdown */}
                {Boolean(anchorEl) && fieldOptions.length > 0 && (
                    <DropdownPopper
                        open={Boolean(anchorEl)}
                        anchorEl={anchorEl}
                        options={fieldOptions.map(opt => ({
                            id: opt.value,
                            label: opt.label,
                            value: opt.value,
                            refNodeId: ''
                        }))}
                        onClose={() => {
                            setAnchorEl(null);
                        }}
                        onOptionClick={(option) => {
                            if (activeRowIndex !== null) {
                                handleKeyChange(activeRowIndex, option.value as string);
                                onSave("", () => {}, true);
                            }
                            setAnchorEl(null);
                        }}
                        groupByKey={undefined}
                    />
                )}

                <div className="flex justify-between mt-4">
                    <Button
                        startIcon={<AddIcon />}
                        onClick={handleAddRow}
                        variant="outlined"
                        size="small"
                    >
                        Add Row
                    </Button>
                </div>
            </div>
        </>
    );
}

export default TriggerSetupNanonetsUpdate;

